// Also loaded by `eslint.config.js`, through Node's native ESM loader rather
// than Nuxt's: keep runtime imports extension-complete (`#core/utils/is.ts`,
// not `#core/utils/is`) and syntax erasable (no `enum`, `namespace`, or
// parameter properties). Nuxt tolerates both mistakes, so only `pnpm lint`
// would break.
import type { NetlifyDeployContext, SupportedBrand, TargetEnv } from '#types/env'

/**
 * Exported so tooling can enumerate every brand/region/country combination, not just the one the
 * current environment selects - `scripts/layers-resolve` needs the whole matrix to answer "which
 * brands override this path".
 */
export const brandLocations = {
  canvas: {},
  thenorthface: {
    emea: [],
    nora: []
  },
  timberland: {
    emea: [],
    nora: []
  },
  vans: {
    emea: [],
    nora: []
  }
}

export const api = {
  mockConfig: process.env.API_MOCK_CONFIG || 'false'
}
export const apiRoutePrefix = process.env.API_ROUTE_PREFIX || '/fapi'
export const brand: SupportedBrand = process.env.BRAND?.toLowerCase() as SupportedBrand || 'canvas'
export const buildDir = process.env.BUILD_NUXT_FOLDER || '.nuxt'
export const outputDir = process.env.BUILD_OUTPUT_FOLDER || '.output'
export const cmsApiRoutePrefix = process.env.CMS_API_ROUTE_PREFIX || '/fcontent'
export const cloudinaryUploadEndpoint = process.env.CLOUDINARY_UPLOAD_ENDPOINT || 'https://api.cloudinary.com/v1_1/vfc-external/image/upload'
export const currency = {
  'cs-cz': 'CZK',
  'da-dk': 'DKK',
  'de-at': 'EUR',
  'de-be': 'EUR',
  'de-ch': 'CHF',
  'de-de': 'EUR',
  'de-lu': 'EUR',
  'el-gr': 'EUR',
  'en-ca': 'CAD',
  'en-ch': 'CHF',
  'en-cz': 'CZK',
  'en-dk': 'DKK',
  'en-fi': 'EUR',
  'en-gb': 'GBP',
  'en-gr': 'EUR',
  'en-ie': 'EUR',
  'en-lu': 'EUR',
  'en-no': 'NOK',
  'en-pt': 'EUR',
  'en-se': 'SEK',
  'en-us': 'USD',
  'es-us': 'USD',
  'es-es': 'EUR',
  'fr-be': 'EUR',
  'fr-ca': 'CAD',
  'fr-ch': 'CHF',
  'fr-fr': 'EUR',
  'fr-lu': 'EUR',
  'it-ch': 'CHF',
  'it-it': 'EUR',
  'nl-be': 'EUR',
  'nl-lu': 'EUR',
  'nl-nl': 'EUR',
  'no-no': 'NOK',
  'pl-pl': 'PLN',
  'pt-pt': 'EUR',
  'sv-fi': 'EUR',
  'sv-se': 'SEK'
}
export const region = process.env.REGION?.toLowerCase()
export const country = process.env.COUNTRY?.toLowerCase()
export const gitHash = process.env.NUXT_PUBLIC_GIT_HASH || 'none'
export const isDev = process.env.NODE_ENV === 'development'
export const isNetlify = process.env.NETLIFY === 'true'
export const isStorybookEnabled = process.env.STORYBOOK === 'true'
export const serverRequestTimeout = process.env.NUXT_PUBLIC_SERVER_REQUEST_TIMEOUT || 20000
export const supportedBrands = Object.keys(brandLocations) as SupportedBrand[]
// Each brand, and the directory holding its layers. Read by both the
// `unoThemedConfigs` Nuxt options and `eslint.config.js`, which cannot import
// each other - so the map lives here, where both already look.
export const brandDirectories = Object.fromEntries(
  supportedBrands.map((_brand) => [_brand, `brands/${_brand}`])
) as Record<SupportedBrand, string>
export const supportedRegions = brandLocations[brand] ? Object.keys(brandLocations[brand]) : []
export const supportedCountries = region && supportedRegions.length ? brandLocations[brand][region] : []
export const isRegionLayerEnabled = region && supportedRegions.includes(region)
export const isCountryLayerEnabled = country && supportedCountries?.includes(country)
export const isElevateEnabled = process.env.ELEVATE_ENABLED
export const targetEnv: TargetEnv = process.env.NUXT_PUBLIC_TARGET_ENV as TargetEnv || 'DEV'
export const disableProxyForNetlify = process.env.DISABLE_PROXY_FOR_NETLIFY === 'true'
export const staticAssetVersion = 2
export const launchDarklyClientKey: string = process.env.LAUNCHDARKLY_CLIENT_KEY || '618d2e61db10c1162486883b'
export const ldSource = process.env.LD_SOURCE || 'feature-flags.local.json'
export const netlifyDeployContext: NetlifyDeployContext = process.env.CONTEXT as NetlifyDeployContext || ''
export const netlifyDeployUrl = process.env.DEPLOY_PRIME_URL || ''
export const optimizelySnippetId = process.env.NETLIFY_OPTIMIZELY_EDGE_DELIVERY_SNIPPET_ID || ''
